'use client'

import { X24 } from '@vinted/monochrome-icons'
import { Vouchers32 } from '@vinted/multichrome-icons'
import { Button, Cell, Icon, Navigation, PromoBanner, Spacer, Text } from '@vinted/web-ui'
import { useEffect, useRef, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useLocation } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'

import { clickEvent, clickListItemEvent, impressionEvent } from '_libs/common/event-tracker/events'
import { MEMBER_PROFILE_URL } from 'constants/routes'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { ContentSource } from 'constants/tracking/content-sources'
import { ListItemContentType } from 'constants/tracking/content-types'
import {
  getBumpsItemsSelectionBanner,
  BumpableItemModel,
  GetBumpsItemsSelectionBannerResp,
} from '@marketplace-web/vas/vas-data'

import { BumpCheckout } from '../../components/Bumps/BumpCheckout'
import useBumpCheckoutContext from '../../hooks/useBumpCheckoutContext'
import useStartBumpCheckout from '../../hooks/useStartBumpCheckout'
import BumpItemsMultiselect from './BumpItemsMultiselect'
import BumpItemThumbnails from './BumpItemThumbnails'
import useBumpableUserItems from './hooks/useBumpableUserItems'

type Props = {
  promoBannerServerData?: GetBumpsItemsSelectionBannerResp | null
  initialItems?: Array<BumpableItemModel> | null
  isEndReached?: boolean
}

const BumpItems = ({ promoBannerServerData, initialItems, isEndReached }: Props) => {
  const { track } = useTracking()
  const { safeBack } = useLocation()
  const translate = useTranslate('bump_items')
  const { user: currentUser } = useSession()
  const webMultiBumpPageServerAbTest = useAbTest('web_multi_bump_page_server')
  const [isExposed, setIsExposed] = useState(false)

  const trackAbTest = useTrackAbTestCallback()

  const seenItems = useRef<Array<number>>([])

  const { startCheckout } = useStartBumpCheckout()
  const { selectedItems } = useBumpCheckoutContext()
  const { items, isLoading, fetchNextPage, endReached } = useBumpableUserItems({
    initialItems,
    isEndReached,
  })

  useEffect(() => {
    if (!items.length) return
    if (isExposed) return

    setIsExposed(true)
    trackAbTest(webMultiBumpPageServerAbTest)
  }, [items, webMultiBumpPageServerAbTest, isExposed, trackAbTest])

  const { fetch: fetchPromoBanner, data: promoBannerClientData } = useFetch(
    getBumpsItemsSelectionBanner,
  )

  useEffect(() => {
    if (promoBannerServerData) return

    fetchPromoBanner()
  }, [promoBannerServerData, fetchPromoBanner])

  const handleItemVisibility: ComponentProps<
    typeof BumpItemsMultiselect
  >['onItemVisible'] = args => {
    const { item, index } = args

    if (seenItems.current.includes(item.data.id)) return

    track(
      impressionEvent({
        id: item.data.id,
        position: index,
        contentType: ListItemContentType.Item,
        contentSource: ContentSource.UserItems,
        itemOwnerId: currentUser?.id,
      }),
    )
    seenItems.current.push(item.data.id)
  }

  const handleItemClick = (item: BumpableItemModel, position: number, isSelected: boolean) => {
    track(
      clickListItemEvent({
        position,
        id: item.id,
        contentType: ListItemContentType.Item,
        contentSource: ContentSource.UserItems,
      }),
    )

    track(
      clickEvent({
        target: isSelected
          ? ClickableElement.AddItemToBundle
          : ClickableElement.RemoveItemFromBundle,
        targetDetails: JSON.stringify({
          item_id: item.id,
        }),
      }),
    )
  }

  const handleCheckout = () => {
    const selectedItemIds = selectedItems.map(item => item.id)

    track(
      clickEvent({
        target: ClickableElement.PushUpBundle,
        targetDetails: selectedItemIds.toString(),
      }),
    )

    startCheckout()
  }

  const renderHeader = () => {
    if (!currentUser) return null

    return (
      <Navigation
        left={
          <Button
            aria={{ 'aria-label': translate('a11y.actions.close') }}
            iconName={X24}
            styling={Button.Styling.Flat}
            inline
            onClick={() => safeBack(MEMBER_PROFILE_URL(currentUser.id))}
          />
        }
      />
    )
  }

  const renderPromoBanner = () => {
    const promoBannerData = promoBannerServerData || promoBannerClientData

    if (!promoBannerData?.banner) return null

    return (
      <>
        <Spacer size={Spacer.Size.Large} />
        <PromoBanner
          title={promoBannerData.banner.title}
          body={promoBannerData.banner.subtitle}
          icon={<Icon name={Vouchers32} />}
          styling={PromoBanner.Styling.Tight}
          testId="bump-promo-banner"
        />
      </>
    )
  }

  const renderContent = () => (
    <SeparatedList separator={<Spacer size={Spacer.Size.Medium} />}>
      <Cell styling={Cell.Styling.Wide}>
        <Text as="h1" text={translate('page_title')} type={Text.Type.Heading} />
      </Cell>
      <BumpItemsMultiselect
        items={items}
        loadItems={fetchNextPage}
        isLoading={isLoading}
        endReached={!!endReached}
        onItemVisible={handleItemVisibility}
        onItemClick={handleItemClick}
      />
    </SeparatedList>
  )

  const renderFooter = () => (
    <Navigation
      left={<BumpItemThumbnails />}
      right={
        <Button
          inline
          text={translate('actions.bump')}
          disabled={!selectedItems.length}
          size={Button.Size.Medium}
          styling={Button.Styling.Filled}
          onClick={() => handleCheckout()}
          testId="items-bump-button"
        />
      }
    />
  )

  return (
    <div className="u-flexbox u-flex-direction-column u-fill-viewport">
      <div className="action-header">
        <div className="container u-ui-margin-vertical-medium">{renderHeader()}</div>
      </div>
      <div className="u-flex-grow">
        <div className="container">
          {renderPromoBanner()}
          {renderContent()}
        </div>
      </div>
      <div className="action-footer">
        <div className="container u-ui-margin-vertical-medium">{renderFooter()}</div>
      </div>
      <BumpCheckout isInitiatedFromMultiSelect />
    </div>
  )
}

export default BumpItems
